import { ShieldCheck } from "lucide-react";
import { PRODUCTS, TRADING_STATEMENT, BUYER_STEPS, SUPPLIER_STEPS } from "../../data/content";
import { FadeUp, SectionHead, ChapterTag } from "./Reveal";

export default function TradingProducts() {
    return (
        <section id="products" className="scroll-mt-20 bg-navy-surface py-24 lg:py-32" data-testid="trading-products-section">
            <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                <ChapterTag>Chapter 05 &mdash; Global Trading Products</ChapterTag>
                <SectionHead
                    className="mt-8"
                    eyebrow="International Trading"
                    title="Connecting Supply with International Demand"
                    copy="Through our international network, Viet Medica Ltd supports introductions and commercial coordination between qualified buyers, suppliers, distributors and authorised partners across selected energy, agricultural and food-product markets."
                />

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {PRODUCTS.map((p, i) => (
                        <FadeUp key={p.id} delay={(i % 2) * 0.1} className="h-full">
                            <article
                                data-testid={`product-card-${p.id}`}
                                className="group flex h-full flex-col overflow-hidden border border-white/8 bg-navy-deep transition-colors duration-500 hover:border-gold/40"
                            >
                                <div className="relative h-52 overflow-hidden">
                                    <img
                                        src={p.image}
                                        alt={p.imageAlt}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/25 to-transparent" aria-hidden="true" />
                                    <h3 className="absolute bottom-4 left-7 font-serif text-3xl font-light text-ivory">
                                        {p.title}
                                    </h3>
                                </div>
                                <div className="flex flex-1 flex-col p-7 lg:p-8">
                                    <p className="text-sm leading-relaxed text-slate-300/85">{p.copy}</p>
                                    <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                                        {p.points.map((pt) => (
                                            <li key={pt} className="flex items-center gap-3 text-[13px] text-slate-300/75">
                                                <span className="h-px w-4 shrink-0 bg-gold" aria-hidden="true" />
                                                {pt}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        </FadeUp>
                    ))}
                </div>

                <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {[
                        { id: "buyers", eyebrow: "For Buyers", title: "How We Work with Buyers", steps: BUYER_STEPS },
                        { id: "suppliers", eyebrow: "For Suppliers", title: "How We Work with Suppliers", steps: SUPPLIER_STEPS },
                    ].map((panel) => (
                        <FadeUp key={panel.id} className="h-full">
                            <div
                                className="h-full border border-white/8 bg-navy-deep p-8 lg:p-10"
                                data-testid={`trading-${panel.id}-steps`}
                            >
                                <span className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold">
                                    {panel.eyebrow}
                                </span>
                                <h3 className="mt-4 font-serif text-2xl font-light text-ivory lg:text-3xl">
                                    {panel.title}
                                </h3>
                                <ol className="mt-7 space-y-4">
                                    {panel.steps.map((s, i) => (
                                        <li key={s} className="flex items-baseline gap-4 text-sm leading-relaxed text-slate-300/85">
                                            <span className="shrink-0 font-serif text-lg text-gold" aria-hidden="true">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            {s}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.1} className="mt-10">
                    <div
                        className="flex items-start gap-4 border border-gold/30 bg-gold/5 p-6 lg:p-7"
                        data-testid="trading-compliance-statement"
                    >
                        <ShieldCheck size={20} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                        <p className="text-sm leading-relaxed text-gold-bright/90">{TRADING_STATEMENT}</p>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
